if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      connectedCallback() {
        if (!this.closest('product-info[id^="MainProduct-"]') || !this.elements.viewer?.slider) return;

        this.mobileMql = window.matchMedia('(max-width: 749px)');
        this.activeMobileMedia = this.elements.viewer.querySelector('.product__media-item.is-active');
        this.mobileMediaObserved = false;
        this.mobileMediaResizeObserver = new ResizeObserver(() => this.updateMobileGalleryHeight());
        this.onMobileSlideChanged = (event) => this.updateMobileGalleryHeight(event.detail?.currentElement);
        this.onMobileMediaLoad = (event) => {
          if (this.activeMobileMedia?.contains(event.target)) this.updateMobileGalleryHeight();
        };
        this.onMobileBreakpointChange = () => this.updateMobileGalleryHeight();
        this.onMobileViewportResize = () => this.updateMobileGalleryHeight();

        this.elements.viewer.addEventListener('slideChanged', this.onMobileSlideChanged);
        this.elements.viewer.addEventListener('load', this.onMobileMediaLoad, true);
        this.mobileMql.addEventListener('change', this.onMobileBreakpointChange);
        window.addEventListener('resize', this.onMobileViewportResize);
        this.updateMobileGalleryHeight();
      }

      disconnectedCallback() {
        this.mobileMediaResizeObserver?.disconnect();
        this.mobileMediaObserved = false;
        this.elements.viewer?.removeEventListener('slideChanged', this.onMobileSlideChanged);
        this.elements.viewer?.removeEventListener('load', this.onMobileMediaLoad, true);
        this.mobileMql?.removeEventListener('change', this.onMobileBreakpointChange);
        window.removeEventListener('resize', this.onMobileViewportResize);
        this.elements.viewer?.slider?.style.removeProperty('height');
      }

      updateMobileGalleryHeight(media) {
        if (!this.mobileMql) return;

        const slider = this.elements.viewer.slider;
        if (!this.mobileMql.matches) {
          slider.style.removeProperty('height');
          this.mobileMediaResizeObserver.disconnect();
          this.mobileMediaObserved = false;
          return;
        }

        const activeMedia =
          (media && slider.contains(media) && media) ||
          (this.activeMobileMedia && slider.contains(this.activeMobileMedia) && this.activeMobileMedia) ||
          slider.querySelector('.product__media-item.is-active, .product__media-item');
        if (!activeMedia) return;

        if (activeMedia !== this.activeMobileMedia || !this.mobileMediaObserved) {
          this.mobileMediaResizeObserver.disconnect();
          this.mobileMediaResizeObserver.observe(activeMedia);
          this.activeMobileMedia = activeMedia;
          this.mobileMediaObserved = true;
        }

        const height = activeMedia.getBoundingClientRect().height;
        if (height > 0) slider.style.height = `${Math.ceil(height)}px`;
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.updateMobileGalleryHeight(activeMedia);

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
